import React, { useCallback, useState } from 'react';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  DialogTitle, DialogContent, DialogActions, TextField, IconButton, Chip,
  Collapse, MenuItem,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LinkIcon from '@mui/icons-material/Link';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from './firebaseConfig';
import Filters from './Filters';
import AppDialog from './components/AppDialog';
import { apiUrl, handleApiError } from './utils/api';
import { formatDate } from './utils/date';

function PaymentRow({ payment, onMatch }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{payment.carrierName || payment.carrierCode}</TableCell>
        <TableCell>{payment.checkNo}</TableCell>
        <TableCell>{formatDate(payment.date)}</TableCell>
        <TableCell align="right">{Number(payment.totalAmount || 0).toFixed(2)}</TableCell>
        <TableCell align="right">
          {(() => {
            const inv = Number(payment.invoicedTotal || 0);
            const v = Number(payment.variance || 0);
            if (inv === 0) return <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>;
            const even = Math.abs(v) < 0.005;
            const color = even ? 'success.main' : v < 0 ? 'error.main' : 'warning.main';
            const label = even ? 'Even' : v < 0 ? 'Short' : 'Over';
            return (
              <Box component="span" title={`Invoiced $${inv.toFixed(2)}`} sx={{ color, fontWeight: 600, whiteSpace: 'nowrap' }}>
                {v.toFixed(2)}{' '}
                <Chip size="small" label={label}
                  color={even ? 'success' : v < 0 ? 'error' : 'warning'} variant="outlined" />
              </Box>
            );
          })()}
        </TableCell>
        <TableCell>
          {payment.status === 'MATCHED' ? (
            <Chip label="Matched" color="success" size="small" />
          ) : payment.status === 'PARTIAL' ? (
            <Chip label="Partial" color="warning" size="small" />
          ) : (
            <Chip label="Unmatched" color="default" size="small" />
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Payment Lines</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Matched Invoice</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(payment.lines || []).map((ln) => (
                    <TableRow key={ln.id}>
                      <TableCell>{ln.description || '—'}</TableCell>
                      <TableCell align="right">{Number(ln.amount || 0).toFixed(2)}</TableCell>
                      <TableCell>{ln.matchedInvoiceId || '—'}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => onMatch(ln)} title="Match to invoice">
                          <LinkIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function Reconciliation() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [matchLine, setMatchLine] = useState(null);
  const [matchInvoiceId, setMatchInvoiceId] = useState('');
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [user] = useAuthState(auth);

  const fetchPayments = async (filterData) => {
    setLoading(true);
    setError('');
    setInfo('');
    setPayments([]);
    try {
      const params = {
        startDate: filterData?.startDate,
        endDate: filterData?.endDate,
        carrierId: filterData?.carrier,
      };
      const response = await axios.get(`${apiUrl}/api/carrier-payments`, { params });
      const data = Array.isArray(response.data) ? response.data : (response.data?.payments || []);
      setPayments(data);
      // Weekly invoices for the same period → the match dropdown.
      try {
        const inv = await axios.get(`${apiUrl}/api/carrier-payments/invoices`, {
          params: { startDate: filterData?.startDate, endDate: filterData?.endDate },
        });
        setInvoiceOptions(Array.isArray(inv.data) ? inv.data : []);
      } catch { setInvoiceOptions([]); }
      if (data.length === 0) setInfo('No matching records found.');
    } catch (err) {
      setError(handleApiError(err, 'Failed to load carrier payments'));
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError('');
    setInfo('');
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        if (user?.email) fd.append('email', user.email);
        await axios.post(`${apiUrl}/api/carrier-payments/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      setInfo(`Uploaded ${files.length} document(s). Refresh filters to view.`);
    } catch (err) {
      setError(handleApiError(err, 'Upload failed'));
    } finally {
      setUploading(false);
    }
  }, [user]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const submitMatch = async () => {
    if (!matchLine || !matchInvoiceId) return;
    try {
      await axios.post(`${apiUrl}/api/reconciliation/match`, {
        paymentLineId: matchLine.id,
        invoiceId: matchInvoiceId,
      });
      setMatchLine(null);
      setMatchInvoiceId('');
      setInfo('Match saved. Re-run filters to refresh.');
    } catch (err) {
      setError(handleApiError(err, 'Match failed'));
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>Carrier Payment Reconciliation</Typography>

      <Paper
        {...getRootProps()}
        sx={{
          p: 3, mb: 3, textAlign: 'center', cursor: 'pointer',
          border: '2px dashed', borderColor: isDragActive ? 'primary.main' : 'grey.400',
        }}
      >
        <input {...getInputProps()} />
        <Typography>
          {uploading ? 'Uploading…' : isDragActive
            ? 'Drop the file here…'
            : 'Drag & drop a Kapavik check / LCR settlement / IVY\'s invoice here, or click to select.'}
        </Typography>
      </Paper>

      <Filters
        ScreenName="Reconciliation"
        onFilterChange={fetchPayments}
        showCarrier
        hideCompany
        hideTruck
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {info && <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert>}
      {loading && <CircularProgress />}

      {payments.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Carrier</TableCell>
                <TableCell>Check / Doc #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Total ($)</TableCell>
                <TableCell align="right">Variance ($)</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((p) => (
                <PaymentRow key={p.id} payment={p} onMatch={setMatchLine} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AppDialog open={Boolean(matchLine)} onClose={() => setMatchLine(null)} fullWidth maxWidth="xs">
        <DialogTitle>Match payment line to invoice</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Line amount: <strong>${Number(matchLine?.amount || 0).toFixed(2)}</strong>
          </Typography>
          <TextField
            select
            autoFocus
            fullWidth
            label="Match to weekly invoice"
            value={matchInvoiceId}
            onChange={(e) => setMatchInvoiceId(e.target.value)}
          >
            {invoiceOptions.length === 0 && (
              <MenuItem value="" disabled><em>No weekly invoices for this period</em></MenuItem>
            )}
            {invoiceOptions.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.scopeType} · {o.scopeName} · wk {o.weekNo} · ${Number(o.total || 0).toFixed(2)}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMatchLine(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitMatch} disabled={!matchInvoiceId}>Match</Button>
        </DialogActions>
      </AppDialog>
    </Box>
  );
}
